import { inject } from '@angular/core';
import {
  CanActivateFn,
  RedirectCommand,
  RedirectFunction,
  Router,
} from '@angular/router';
import { UserGroup } from '@core/api/angular';
import { MAP_ROOT } from '@lassi/config';
import { AuthService, AuthSteps } from '@lassi/webapp-shared';
import { map } from 'rxjs';

export const authStepRedirectFn =
  (step: AuthSteps): RedirectFunction =>
  () => {
    const router = inject(Router);
    return router.createUrlTree(['/login'], {
      queryParamsHandling: 'merge',
      queryParams: {
        step,
      },
    });
  };

export const logoutRedirectFn: RedirectFunction = (redirectData) => {
  const redirectTo = redirectData.queryParams['redirectTo'];

  return redirectTo?.startsWith('/map') ? (redirectTo ?? '/') : '/';
};

export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isLoggedIn$.pipe(
    map((loggedIn) =>
      loggedIn
        ? true
        : new RedirectCommand(router.createUrlTree(['/']), {
            replaceUrl: true,
          }),
    ),
  );
};

export const smesSubmissionRedirectFn: RedirectFunction = (redirectData) => {
  const queryParams = new URLSearchParams(redirectData.queryParams);
  return `/map/(menu:landing/smes/mark-submissions/${redirectData.params['lodgeStatus']}/${redirectData.params['submissionId']})?${queryParams.toString()}`;
};

export const nonLoggedInGuard: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isLoggedIn$.pipe(
    map((loggedIn) => {
      if (!loggedIn) return true;

      const [redirectTo, searchParams = ''] = (
        route.queryParams['redirectTo'] || '/'
      ).split('?');
      const queryParams = new URLSearchParams('?' + searchParams);

      const urlTree = router.createUrlTree([redirectTo], {
        queryParams: Object.fromEntries(queryParams.entries()),
      });

      return new RedirectCommand(urlTree, {
        replaceUrl: true,
      });
    }),
  );
};

export const userRoleGuard = (
  role: UserGroup | Array<UserGroup>,
  redirectTo?: string,
): CanActivateFn => {
  return () => {
    const router = inject(Router);
    const mapRoot = inject(MAP_ROOT);

    return inject(AuthService)
      .canActivate(role)
      .pipe(
        map((result) => {
          return (
            result ||
            router.createUrlTree([redirectTo ?? mapRoot], {
              queryParamsHandling: 'preserve',
            })
          );
        }),
      );
  };
};
